// src/pages/ProductDetail.js
import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useParams, useOutletContext, useLocation, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useUser } from '../context/UserContext';
import { useProducts } from '../hooks/useProducts';
import API from '../api';
import { safeEncodeURIComponent, parseProductParams, generateProductUrl } from '../utils/urlEncoding';
import { generateCategoryUrl } from '../utils/enhancedLinkGenerators';
import { userLogger } from '../utils/userLogger';
import ZoomableImage from '../components/ZoomableImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAmazon } from '@fortawesome/free-brands-svg-icons';
import './ProductDetail.css';
import './ProductDetail.mobile.css'; // Mobile CSS loaded first
import './ProductDetail.desktop.css'; // Desktop-specific overrides loaded LAST to take precedence

const currencySymbols = {
  usa: '$ ', // Added space for consistency
  canada: 'CAD $',
  india: '₹ ', // Added space
  uk: '£ ' // Added space
};

// Helper to map to ISO 4217 currency codes
const getPriceCurrency = (countryCode) => {
  const map = {
    usa: 'USD',
    canada: 'CAD',
    india: 'INR',
    uk: 'GBP'
  };
  return map[countryCode?.toLowerCase()] || 'USD'; // Default to USD if unknown
};

function ProductDetail() {
  const params = useParams();
  const { productId: id, productName } = parseProductParams(params);
  const { countryCode } = useOutletContext();
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const siteBaseUrl = 'https://www.originselect.com';
  const { user } = useUser();
  const [isLoved, setIsLoved] = useState(false);
  const [showLoginMessage, setShowLoginMessage] = useState(false);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const shareDropdownRef = useRef(null);

  // State-based mobile detection for reliable layout control
  const [isMobile, setIsMobile] = useState(false);
  
  useLayoutEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 480; // Changed from 767 to 480 for mobile-only
      setIsMobile(mobile);
    };
    
    // Check immediately
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Force re-render when navigating to ensure mobile state is fresh
  useLayoutEffect(() => {
    const mobile = window.innerWidth <= 480; // Changed from 767 to 480 for mobile-only
    setIsMobile(mobile);
    
    // Apply critical data attributes immediately for CSS targeting
    if (mobile) {
      document.documentElement.setAttribute('data-mobile', 'true');
      document.documentElement.classList.add('is-product-detail');
      document.body.setAttribute('data-route', location.pathname);
      
      // Force immediate reflow to ensure CSS application
      void document.body.offsetHeight;
      
      // Additional timeout to handle late-loading styles
      setTimeout(() => {
        void document.body.offsetHeight;
      }, 50);
    } else {
      document.documentElement.removeAttribute('data-mobile');
      document.documentElement.classList.remove('is-product-detail');
      document.body.removeAttribute('data-route');
    }
    
    // Cleanup function to remove attributes when component unmounts
    return () => {
      document.documentElement.removeAttribute('data-mobile');
      document.documentElement.classList.remove('is-product-detail');
      document.body.removeAttribute('data-route');
    };
  }, [id, location.pathname]);

  // Use our caching hook for better performance
  const {
    data: product,
    loading,
    error: productError,
    refetch
  } = useProducts({
    endpoint: `/api/products/${encodeURIComponent(id)}`,
    params: {},
    useCache: true,
    cacheDuration: 10 // Cache product details for 10 minutes
  });

  // Handle any errors from the hook
  useEffect(() => {
    if (productError) {
      setError({
        message: 'Failed to load product details. Please try again later.',
        details: productError.message
      });
    } else {
      setError(null);
    }
  }, [productError]);
  
  // Prefetch related products when this product loads
  const prefetchRelated = useCallback(async () => {
    if (!product || !product.category) return;
    
    try {
      // Prefetch other products in the same category but don't display them yet
      await API.get('/api/products/by-category-and-countries', {
        params: {
          category: product.category,
          countries: product.country || countryCode,
        },
      });
    } catch (err) {
      // Silent fail - this is just an optimization
      console.debug('Failed to prefetch related products', err);
    }
  }, [product, countryCode]);
  
  // Call prefetch when product data is available
  useEffect(() => {
    if (product && product._id) {
      prefetchRelated();
    }
  }, [product, prefetchRelated]);

  // SEO redirect: If we have product data but no product name in URL, redirect to SEO-friendly URL
  useEffect(() => {
    if (product && product.name && !productName && typeof window !== 'undefined') {
      // Only redirect if this is not the initial load from a direct link
      // and the browser supports history API
      const currentPath = location.pathname;
      const expectedPath = generateProductUrl(countryCode, id, product.name);
      
      if (currentPath !== expectedPath && currentPath.includes('/products/' + id) && !currentPath.includes('/name/')) {
        // Use replace to avoid creating duplicate history entries
        navigate(expectedPath, { replace: true });
      }
    }
  }, [product, productName, id, countryCode, location.pathname, navigate]);

  const handleBackClick = (e) => {
    e.preventDefault();
    
    const fromSearch = sessionStorage.getItem('fromSearch') === 'true';
    const storedReferrer = sessionStorage.getItem('productReferrer');
    
    // First priority: If we came from search page and have a referrer
    if (fromSearch && storedReferrer && storedReferrer.includes('/search')) {
      navigate(storedReferrer);
      return;
    }
    
    // Second priority: React Router state
    if (location.state?.from) {
      navigate(location.state.from);
      return;
    }
    
    // Third priority: Generic stored referrer with full URL path
    if (storedReferrer) {
      // Use navigate instead of window.location to keep React Router state
      navigate(storedReferrer);
      return;
    }
    
    // If we have category/subcategory saved, include them in the URL
    const selectedCategory = sessionStorage.getItem('selectedCategory');
    const selectedSubCategory = sessionStorage.getItem('selectedSubCategory');
    
    let productsPath = `/country/${countryCode}/products`;
    
    if (selectedCategory) {
      productsPath += `?category=${safeEncodeURIComponent(selectedCategory)}`;
      if (selectedSubCategory) {
        productsPath += `&subcategory=${safeEncodeURIComponent(selectedSubCategory)}`;
      }
    }
    
    // Last resort: Go back to products page
    navigate(productsPath);
  };

  // const navigateToCategory = (e, category) => {
  //   e.preventDefault();
  //   navigate(`/country/${countryCode}/products?category=${encodeURIComponent(category)}`);
  // };

  // const navigateToSubcategory = (e, category, subcategory) => {
  //   e.preventDefault();
  //   navigate(`/country/${countryCode}/products?category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(subcategory)}`);
  // };

  // const navigateToProducts = (e) => {
  //   e.preventDefault();
  //   navigate(`/country/${countryCode}/products`);
  // };

  // Format country name for display
  const getCountryName = (code) => {
    if (!code) return '';
    
    const countryNames = {
      'canada': 'Canada',
      'usa': 'United States',
      'india': 'India',
      'uk': 'United Kingdom'
    };
    
    return countryNames[code.toLowerCase()] || code;
  };

  // Check if the current product is loved by the user
  useEffect(() => {
    const checkLoveStatus = async () => {
      if (user && product?._id) {
        try {
          const response = await API.get(`/api/loved-products/check/${product._id}/${user.email}`, {
            headers: {
              'Accept': 'application/json'
            },
            // Use a reasonable timeout
            timeout: 5000,
            // Skip cache to get fresh love status
            skipCache: true
          });
          
          if (response.data && typeof response.data === 'object') {
            setIsLoved(!!response.data.isLoved);
          } else {
            console.warn('Invalid love status response format:', response.data);
            // Default to not loved if response format is invalid
            setIsLoved(false);
          }
        } catch (err) {
          console.error('Error checking love status:', err);
          // Default to not loved on error - this is less disruptive than showing an error
          setIsLoved(false);
        }
      }
    };
    
    checkLoveStatus();
  }, [user, product]);
  
  // Handle clicking the love button
  const handleLoveClick = async () => {
    if (!user) {
      setShowLoginMessage(true);
      setTimeout(() => setShowLoginMessage(false), 3000);
      return;
    }
    
    try {
      console.log('Toggling love status for product:', product._id);
      
      // Toggle the state immediately for better UX
      const newLovedState = !isLoved;
      setIsLoved(newLovedState);
      
      // Set headers to ensure proper content type handling
      const response = await API.post('/api/loved-products/toggle', 
        {
          productId: product._id,
          email: user.email
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          // Add timeout to prevent long-hanging requests
          timeout: 8000
        }
      );
      
      // Add safety check for response format
      if (response.data && typeof response.data === 'object') {
        console.log('Love toggle success:', response.data);
        // Update state with server response (should match our prediction)
        setIsLoved(response.data.isLoved);
      } else {
        console.error('Invalid API response format:', response.data);
        // Keep the optimistically updated state since the server response is invalid
      }
    } catch (err) {
      console.error('Error toggling love status:', err);
      // Show brief error message but don't revert UI state to prevent jarring UX
      const errorMessage = err.response?.data?.message || 'Server error. Try again later.';
      
      // Use a gentle notification instead of altering the UI state
      console.warn(`Product love toggle error: ${errorMessage}`);
      
      // Revert state after a short delay to prevent immediate UI flicker (CLS)
      setTimeout(() => {
        setIsLoved(!isLoved);
      }, 500);
    }
  };
  
  // Toggle share dropdown or directly use native sharing on mobile
  const handleShareClick = () => {
    // Log share button click (intent to share)
    console.log('📤 SHARE_INTENT', {
      timestamp: new Date().toISOString(),
      productId: product._id,
      productName: product.name,
      userEmail: user?.email || 'anonymous',
      action: 'share_button_clicked'
    });

    // Track share intent in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'share_intent', {
        content_type: 'product',
        item_id: product._id,
        event_category: 'Product',
        event_label: `${product.name} - Share Button Clicked`
      });
    }

    // Always show dropdown for mobile (with both native share and copy link options)
    // This ensures users always have a way to copy the link
    setShowShareDropdown(prev => !prev);
  };
  
  // Handle native sharing
  const handleNativeShare = () => {
    if (navigator.share) {
      // Log share event
      console.log('📤 PRODUCT_SHARE', {
        timestamp: new Date().toISOString(),
        productId: product._id,
        productName: product.name,
        shareMethod: 'native_share',
        userEmail: user?.email || 'anonymous',
        url: window.location.href
      });

      userLogger.logProductShare({
        productId: product._id,
        productName: product.name,
        shareMethod: 'native_share',
        userEmail: user?.email || 'anonymous',
        productUrl: window.location.href
      });

      // Track in Google Analytics
      if (window.gtag) {
        window.gtag('event', 'share', {
          method: 'native',
          content_type: 'product',
          item_id: product._id,
          event_category: 'Product',
          event_label: product.name
        });
      }

      navigator.share({
        title: `${product.name} - OriginSelect`,
        text: `Check out this ${product.name} on OriginSelect!`,
        url: window.location.href
      })
      .then(() => {
        console.log('Share successful');
        // Always close the dropdown/modal after successful sharing
        setShowShareDropdown(false);
      })
      .catch(err => {
        console.error('Share failed:', err);
        // Do not close the modal on error so user can try again
      });
    } else {
      // If navigator.share is not available, just keep the dropdown/modal open
      console.log('Native sharing not supported');
    }
  };
  
  // Handle copy link to clipboard
  const handleCopyLink = () => {
    // Log copy link event
    console.log('📤 PRODUCT_SHARE', {
      timestamp: new Date().toISOString(),
      productId: product._id,
      productName: product.name,
      shareMethod: 'copy_link',
      userEmail: user?.email || 'anonymous',
      url: window.location.href
    });

    userLogger.logProductShare({
      productId: product._id,
      productName: product.name,
      shareMethod: 'copy_link',
      userEmail: user?.email || 'anonymous',
      productUrl: window.location.href
    });

    // Track in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'share', {
        method: 'copy_link',
        content_type: 'product',
        item_id: product._id,
        event_category: 'Product',
        event_label: product.name
      });
    }

    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        // Create a temporary feedback element instead of using alert()
        const feedbackEl = document.createElement('div');
        feedbackEl.textContent = 'Link copied to clipboard!';
        feedbackEl.style.position = 'fixed';
        feedbackEl.style.bottom = '20px';
        feedbackEl.style.left = '50%';
        feedbackEl.style.transform = 'translateX(-50%)';
        feedbackEl.style.backgroundColor = 'rgba(51, 51, 51, 0.9)';
        feedbackEl.style.color = 'white';
        feedbackEl.style.padding = '12px 20px';
        feedbackEl.style.borderRadius = '4px';
        feedbackEl.style.zIndex = '1000';
        feedbackEl.style.fontSize = '14px';
        
        document.body.appendChild(feedbackEl);
        
        // Close the share dropdown
        setShowShareDropdown(false);
        
        // Remove the feedback message after 2 seconds
        setTimeout(() => {
          document.body.removeChild(feedbackEl);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        alert('Failed to copy link. Please try again.');
      });
  };

  // Handle store link click tracking
  const handleBuyFromStoreClick = (e) => {
    // Track in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'click', {
        event_category: 'Ecommerce',
        event_label: `Buy from Store: ${product.name}`,
        event_action: 'buy_button_click',
        item_id: product._id,
        item_name: product.name,
        item_category: product.category || 'Uncategorized',
        item_variant: 'Store',
        price: product.price || 0,
        currency: getPriceCurrency(product.country || countryCode)
      });
    }
    
    // Log using userLogger if available
    if (userLogger && userLogger.logPurchaseIntent) {
      userLogger.logPurchaseIntent({
        productId: product._id,
        productName: product.name,
        price: product.price,
        currency: getPriceCurrency(product.country || countryCode),
        source: 'store',
        url: product.storeLink,
        userEmail: user?.email || 'anonymous'
      });
    }
    
    // Log to console for debugging
    console.log('📊 STORE_CLICK', {
      timestamp: new Date().toISOString(),
      productId: product._id,
      productName: product.name,
      price: product.price,
      currency: getPriceCurrency(product.country || countryCode),
      url: product.storeLink,
      userEmail: user?.email || 'anonymous'
    });
  };
  
  // Handle affiliate link click tracking
  const handleAffiliateLinkClick = (link) => {
    const affiliateName = link.name || 'Affiliate';
    const isAmazon = affiliateName.toLowerCase().includes('amazon');
    
    // Track in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'click', {
        event_category: 'Ecommerce',
        event_label: `Buy on ${affiliateName}: ${product.name}`,
        event_action: 'affiliate_click',
        item_id: product._id,
        item_name: product.name,
        item_category: product.category || 'Uncategorized',
        item_variant: affiliateName,
        price: product.price || 0,
        affiliate_platform: isAmazon ? 'Amazon' : affiliateName,
        currency: getPriceCurrency(product.country || countryCode)
      });
    }
    
    // Log using userLogger if available
    if (userLogger && userLogger.logPurchaseIntent) {
      userLogger.logPurchaseIntent({
        productId: product._id,
        productName: product.name,
        price: product.price,
        currency: getPriceCurrency(product.country || countryCode),
        source: isAmazon ? 'amazon' : 'affiliate',
        affiliate: affiliateName,
        url: link.url,
        userEmail: user?.email || 'anonymous'
      });
    }
    
    // Log to console for debugging
    console.log('📊 AFFILIATE_CLICK', {
      timestamp: new Date().toISOString(),
      productId: product._id,
      productName: product.name,
      price: product.price,
      currency: getPriceCurrency(product.country || countryCode),
      affiliate: affiliateName,
      url: link.url,
      userEmail: user?.email || 'anonymous'
    });
  };

  // Close share dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target)) {
        setShowShareDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shareDropdownRef]);

  if (loading) {
    return (
      <div className="product-detail-page loading">
        <div className="container">
          <div className="product-detail-container skeleton-container">
            <div className="product-image-column">
              <div className="skeleton product-image-skeleton"></div>
              <div className="skeleton-tags">
                <div className="skeleton tag-skeleton"></div>
                <div className="skeleton tag-skeleton"></div>
                <div className="skeleton tag-skeleton"></div>
              </div>
            </div>
            <div className="product-info-column">
              <div className="skeleton title-skeleton"></div>
              <div className="skeleton price-skeleton"></div>
              <div className="skeleton description-skeleton"></div>
              <div className="skeleton description-skeleton"></div>
              <div className="skeleton button-skeleton"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="product-detail-page error">
        <div className="container">
          <div className="error-message">
            <h3>Error Loading Product</h3>
            <p>{error.message}</p>
            <div className="error-actions">
              {/* <a href="#" onClick={handleBackClick} className="btn back-btn"> */}
              <button onClick={handleBackClick} className="btn back-btn">
                Back to Products
              </button>
              <button
                className="btn retry-btn"
                onClick={() => refetch()}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product || !product._id) {
    // It's important to handle the case where product is not yet loaded or not found
    // before trying to generate structured data that depends on it.
    // The existing loading and error states already handle this.
    // If product is null after loading and no error, show "Product not found".
    if (!loading && !error) {
      return <div className="product-detail-page error">
        <div className="container">
          <p className="not-found">Product not found.</p>
          {/* <a href="#" onClick={handleBackClick} className="btn back-btn"> */}
          <button onClick={handleBackClick} className="btn back-btn">
            Back to Products
          </button>
        </div>
      </div>;
    }
    // If still loading or there was an error, the existing returns will handle it.
    // Return null or a loader here if product is null and still loading.
    return loading ? (
      <div className="product-detail-page loading">
        {/* ... skeleton loader ... */}
      </div>
    ) : null; 
  }

  // --- BEGIN MODIFICATION ---
  // Generate a more descriptive meta description
  let metaDescription = product.description;
  if (!metaDescription) {
    let generatedDesc = `${product.name}`;
    if (product.category) {
      generatedDesc += ` from our ${product.category} collection`;
      if (product.subCategory) {
        generatedDesc += ` - ${product.subCategory}`;
      }
    }
    generatedDesc += `. Authentic, handcrafted quality at OriginSelect.`;
    // Trim to a reasonable length for meta descriptions (e.g., 155-160 characters)
    metaDescription = generatedDesc.length > 160 ? generatedDesc.substring(0, 157) + '...' : generatedDesc;
  }
  // --- END MODIFICATION ---

  // Structured Data for Product
  const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${siteBaseUrl}${location.pathname}`, // URL of the current product page
    name: product.name,
    image: product.imageUrl || `${siteBaseUrl}/assets/no-image.png`,
    // --- MODIFICATION ---
    description: metaDescription, // Use the generated or existing description
    // --- END MODIFICATION ---
    sku: product._id, // Using _id as SKU
    // Assuming brand is the site name if not available per product
    brand: {
      '@type': 'Brand',
      name: 'OriginSelect' 
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: getPriceCurrency(product.country || countryCode),
      price: product.price?.toFixed(2),
      // Assuming InStock if price is available. Adjust if you have availability data.
      availability: product.price && product.price > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      url: product.storeLink || `${siteBaseUrl}${location.pathname}`, // URL to the product or offer
      seller: {
        '@type': 'Organization',
        name: 'OriginSelect' // Or the actual seller if different
      }
    }
    // Add other properties like review, aggregateRating if available
  };

  // Structured Data for Breadcrumbs
  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${siteBaseUrl}/country/${countryCode}/home`
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Products',
      item: `${siteBaseUrl}/country/${countryCode}/products`
    }
  ];

  if (product.category) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: product.category,
      item: `${siteBaseUrl}${generateCategoryUrl(countryCode, product.category)}`
    });
  }

  if (product.category && product.subCategory) {      breadcrumbItems.push({
        '@type': 'ListItem',
        position: 4,
        name: product.subCategory,
        item: `${siteBaseUrl}${generateCategoryUrl(countryCode, product.category, product.subCategory)}`
      });
    // Add current product as the last item if subCategory exists
    breadcrumbItems.push({
        '@type': 'ListItem',
        position: 5,
        name: product.name, // Current product name
        item: `${siteBaseUrl}${location.pathname}` // URL of the current product page
      });
  } else if (product.category) {
    // Add current product as the last item if only category exists
     breadcrumbItems.push({
        '@type': 'ListItem',
        position: 4,
        name: product.name, // Current product name
        item: `${siteBaseUrl}${location.pathname}` // URL of the current product page
      });
  } else {
    // Add current product as the last item if no category/subcategory
    breadcrumbItems.push({
        '@type': 'ListItem',
        position: 3,
        name: product.name, // Current product name
        item: `${siteBaseUrl}${location.pathname}` // URL of the current product page
      });
  }
  
  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems
  };

  // The newly redesigned return statement
  return (
    <React.Fragment>
      <Helmet>
        <title>{`${product.name} - OriginSelect`}</title>
        {/* --- MODIFICATION --- */}
        <meta name="description" content={metaDescription} />
        {/* Open Graph meta tags for social media sharing */}
        <meta property="og:title" content={`${product.name} - OriginSelect`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={product.imageUrl || `${siteBaseUrl}/assets/no-image.png`} />
        <meta property="og:url" content={`${siteBaseUrl}${location.pathname}`} />
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.name} - OriginSelect`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={product.imageUrl || `${siteBaseUrl}/assets/no-image.png`} />
        {/* --- END MODIFICATION --- */}
        <script type="application/ld+json">
          {JSON.stringify(productStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
        {/* Add other meta tags like canonical URL if needed */}
        <link rel="canonical" href={`${siteBaseUrl}${location.pathname}`} />
      </Helmet>
      <div className={`product-detail-page theme-${countryCode}`}>
        <div className="container">
          {/* Breadcrumb navigation */}
          <div className="breadcrumb" style={isMobile ? { 
            marginBottom: '1rem',  // Reduced from 2rem to 1rem for mobile
            fontSize: '0.8rem'     // Reduced font size from 0.9rem to 0.8rem
          } : {}}>
            <Link to={`/country/${countryCode}/home`}>Home</Link>
            <span className="separator">›</span>
            {/* <a href="#" onClick={navigateToProducts}>Products</a> */}
            <Link to={`/country/${countryCode}/products`}>Products</Link>
            {product.category && (
              <>
                <span className="separator">›</span>
                {/* <a href="#" onClick={(e) => navigateToCategory(e, product.category)}>{product.category}</a> */}
                <Link to={generateCategoryUrl(countryCode, product.category)}>{product.category}</Link>
              </>
            )}
            {product.subCategory && (
              <>
                <span className="separator">›</span>
                {/* <a href="#" onClick={(e) => navigateToSubcategory(e, product.category, product.subCategory)}>{product.subCategory}</a> */}
                <Link 
                  to={generateCategoryUrl(countryCode, product.category, product.subCategory)}
                  className="subcategory-link"
                >
                  {product.subCategory}
                </Link>
              </>
            )}
          </div>

          <div 
            className="product-detail-container"
            style={isMobile ? {
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem', // Further reduced from 0.75rem to 0.5rem
              gridTemplateColumns: 'none',
              marginBottom: '1.25rem' // Further reduced from 1.5rem to 1.25rem
            } : {}}
          >
            {/* Left column - Image and tags */}
            <div className="product-image-column">
              <div className="product-detail-image">
                <ZoomableImage
                  src={product.imageUrl || '/assets/no-image.png'}
                  alt={product.name}
                  style={isMobile ? {
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '85%',
                    maxHeight: '230px', // Reduced from 250px to 230px
                    objectFit: 'contain',
                    margin: '0 auto',
                    display: 'block'
                  } : {
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '90%',
                    maxHeight: '550px',
                    minHeight: '350px',
                    objectFit: 'contain',
                    margin: '0 auto',
                    display: 'block'
                  }}
                  loading="eager"
                  isMobile={isMobile}
                />
              </div>
              
              {/* Tags section - below the product image */}
              <div className="product-tags" style={{ 
                marginTop: isMobile ? '0.3rem' : '1rem', // Further reduced from 0.5rem to 0.3rem
                marginBottom: isMobile ? '0.3rem' : '1rem', // Further reduced from 0.5rem to 0.3rem
                width: '100%' 
              }}>
                {/* Always display these default tags */}
                <span className="tag">{product.category || 'Home Decor'}</span>
                <span className="tag">{product.subCategory || 'Decoration'}</span>
                <span className="tag">Made in {getCountryName(product.country)}</span>
                {product.price > 100 && <span className="tag">Premium</span>}
              </div>
            </div>

            {/* Right column - Information */}
            <div className="product-info-column">
              <div className="product-detail-info">
                {/* Product header without country badge */}
                <div className="product-header" style={isMobile ? { marginBottom: '0.3rem' } : {}}>
                  <div 
                    className="product-title-row"
                    style={isMobile ? {
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '0.3rem' // Further reduced from 0.5rem to 0.3rem
                    } : {}}
                  >
                    {/* Conditionally styled heading based on mobile state */}
                    <div 
                      style={{
                        display: 'block',
                        fontSize: isMobile ? '1.25rem' : '32px', 
                        fontWeight: '600',
                        lineHeight: isMobile ? '1.4' : '1.3',
                        margin: isMobile ? '0 0 0.3rem' : '0 0 1rem', // Further reduced from 0.5rem to 0.3rem
                        width: isMobile ? 'calc(100% - 80px)' : 'calc(100% - 100px)',
                        color: 'black',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      <span
                        role="heading"
                        aria-level="1"
                        style={{
                          fontSize: 'inherit',
                          fontWeight: 'inherit',
                          lineHeight: 'inherit',
                          color: 'inherit',
                          display: 'block'
                        }}
                      >{product.name}</span>
                    </div>
                    <div 
                      className="product-actions"
                      style={isMobile ? {
                        display: 'flex',
                        gap: '0.3rem', // Further reduced from 0.5rem to 0.3rem
                        marginLeft: 'auto'
                      } : {}}
                    >
                      <button 
                        className={`love-button ${isLoved ? 'loved' : ''}`} 
                        onClick={handleLoveClick}
                        aria-label={isLoved ? 'Remove from favorites' : 'Add to favorites'}
                        style={{ color: isLoved ? '#E32D2D' : '#666' }}
                      >
                        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill={isLoved ? "#E32D2D" : "none"} strokeLinecap="round" strokeLinejoin="round" style={{ fill: isLoved ? '#E32D2D' : 'none' }}>
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                        </svg>
                      </button>
                      <div className="share-container" ref={shareDropdownRef}>
                        <button 
                          className="share-button"
                          onClick={handleShareClick}
                          aria-label="Share product"
                          aria-expanded={showShareDropdown}
                        >
                          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3"></circle>
                            <circle cx="6" cy="12" r="3"></circle>
                            <circle cx="18" cy="19" r="3"></circle>
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                          </svg>
                        </button>
                      
                      {showShareDropdown && (
                        isMobile ? (
                          <div className="share-modal-overlay">
                            <div className="share-modal">
                              <div className="share-modal-header">
                                Share this product
                              </div>
                              
                              <div className="share-modal-content">
                                {navigator.share && (
                                  <button 
                                    onClick={handleNativeShare}
                                    className="share-option"
                                  >
                                    <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                                      <polyline points="16 6 12 2 8 6"></polyline>
                                      <line x1="12" y1="2" x2="12" y2="15"></line>
                                    </svg>
                                    <span>Share via...</span>
                                  </button>
                                )}
                                
                                <button 
                                  onClick={handleCopyLink}
                                  className="share-option"
                                >
                                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                  </svg>
                                  <span>Copy Link</span>
                                </button>
                              </div>
                              
                              <button
                                onClick={() => setShowShareDropdown(false)}
                                className="share-modal-cancel"
                                style={{
                                  width: '100%',
                                  background: '#fff',
                                  border: 'none',
                                  borderTop: '1px solid #eee',
                                  padding: '16px',
                                  fontSize: '16px',
                                  fontWeight: '500',
                                  color: '#333',
                                  cursor: 'pointer',
                                  textAlign: 'center',
                                  display: 'block'
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="share-dropdown">
                            <button onClick={handleNativeShare} className="share-option">
                              <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                                <polyline points="16 6 12 2 8 6"></polyline>
                                <line x1="12" y1="2" x2="12" y2="15"></line>
                              </svg>
                              <span>Share</span>
                            </button>
                            <button onClick={handleCopyLink} className="share-option">
                              <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                              <span>Copy Link</span>
                            </button>
                          </div>
                        )
                      )}
                      </div>
                    </div>
                  </div>
                  {showLoginMessage && (
                    <div className="login-message">
                      Please sign in with Google to love this product
                    </div>
                  )}
                </div>
                
                {/* Product price - updated format to match the provided image */}
                {product.price != null && product.price > 0 && (
                  <div className="price-container" style={isMobile ? { marginBottom: '0.5rem' } : {}}>
                    <div className="price-wrapper">
                      <div className="price" style={isMobile ? { marginTop: '0.3rem', marginBottom: '0.2rem' } : {}}>
                        <span className="currency">{currencySymbols[product.country?.toLowerCase()] || '$'}</span>
                        {product.price.toFixed(2)}
                      </div>
                      
                      {/* Mobile-optimized disclaimer */}
                      <div className="mobile-disclaimer">
                        {/* <a href="/disclaimer" className="disclaimer-link" title="View our disclaimer"> */}
                        <Link to="/disclaimer" className="disclaimer-link" title="View our disclaimer">
                          Disclaimer
                        </Link>
                      </div>
                    </div>
                    
                    {/* Desktop disclaimer - hidden on mobile */}
                    <div className="price-disclaimer desktop-disclaimer">
                      Please check the store for current pricing and availability. 
                      {/* <a  */}
                      {/*  href="/disclaimer"  */}
                      {/*  className="disclaimer-link" */}
                      {/*  title="View our disclaimer">Disclaimer */}
                      {/* </a> */}
                      <Link to="/disclaimer" className="disclaimer-link" title="View our disclaimer">Disclaimer</Link>
                    </div>
                  </div>
                )}

                {/* Product description */}
                {product.description && (
                  <div className="description" style={isMobile ? { marginBottom: '0.5rem', fontSize: '0.95rem' } : {}}>
                    {product.description}
                  </div>
                )}
                
                {/* Store + Affiliate Links */}
                <div className="affiliate-links" style={isMobile ? { gap: '0.5rem' } : {}}>
                  {product.storeLink && (
                    <a
                      href={product.storeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="store-link"
                      style={isMobile ? { padding: '0.6rem 1rem', fontSize: '0.95rem' } : {}}
                      onClick={handleBuyFromStoreClick}
                    >
                      Buy from Store
                    </a>
                  )}

                  {product.affiliateLinks?.length > 0 &&
                    product.affiliateLinks.map((link, i) => {
                      const isAmazon = link.name?.toLowerCase().includes('amazon');
                      return (
                        <a
                          key={i}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`affiliate-btn ${isAmazon ? 'amazon-btn' : ''}`}
                          style={isMobile ? { padding: '0.6rem 1rem', fontSize: '0.95rem' } : {}}
                          onClick={() => handleAffiliateLinkClick(link)}
                        >
                          {isAmazon && (
                            <FontAwesomeIcon 
                              icon={faAmazon}
                              className="amazon-icon" 
                              style={{ marginRight: '8px', fontSize: '18px' }}
                            />
                          )}
                          Buy on {link.name || 'Affiliate'}
                        </a>
                      );
                    })
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default ProductDetail;
